import { Badge, Card, Group, Table } from '@mantine/core';
import { IconPencil, IconPlus, IconTrash } from '@tabler/icons-react';
import { useEffect, useState } from 'react';

import { useSelector } from 'react-redux';
import prettifiedLabel from '../../constants/labelPrettifier';
import useEventTypes from '../../helpers/useEventTypes';
import useFetch from '../../helpers/useFetch';
import ErrorMessage from '../ErrorMessage';
import LockableButton from '../Lockable/LockableButton';
import ConfirmationModal from '../Modals/ConfirmationModal';
import EventIcon from '../Profiles/EventIcon';
import Spinner from '../Spinner';
import LoyaltyGrantModal from './LoyaltyGrantModal';

const LoyaltyGrantsTab = () => {
  const { domain } = useSelector((state) => state.domain);
  const fetch = useFetch();

  const [loyaltyGrants, setLoyaltyGrants] = useState();
  const [loyaltyGrant, setLoyaltyGrant] = useState();
  const [laodError, setLoadError] = useState();
  const [userError, setUserError] = useState();
  const [showDeleteId, setShowDeleteId] = useState();
  const eventTypes = useEventTypes(domain.accountId, domain.id, setLoadError);

  useEffect(() => {
    fetch(`/api/admin/accounts/${domain.accountId}/domains/${domain.id}/loyalty/grants`)
      .then(({ loyaltyGrants }) => {
        setLoyaltyGrants(loyaltyGrants);
      })
      .catch(setLoadError);
  }, [domain.accountId, domain.id, fetch]);

  if (laodError) {
    return <ErrorMessage message={laodError.message} />;
  }

  if (!loyaltyGrants || !eventTypes) {
    return <Spinner />;
  }

  const del = (cb) => {
    setUserError(undefined);

    fetch(`/api/admin/accounts/${domain.accountId}/domains/${domain.id}/loyalty/grants/${showDeleteId}`, {
      method: 'DELETE',
    })
      .then(() => {
        setLoyaltyGrants(loyaltyGrants.filter((c) => c.id !== showDeleteId));
        cb();
      })
      .catch(setUserError);
  };

  return (
    <>
      <ConfirmationModal
        show={!!showDeleteId}
        close={() => setShowDeleteId(undefined)}
        confirm={del}
        message="This action cannot be undone!"
      />
      <Card withBorder>
        <Card.Section inheritPadding py="md">
          {loyaltyGrants.length > 0 && (
            <Table striped withTableBorder highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Action</Table.Th>
                  <Table.Th>Tags</Table.Th>
                  <Table.Th>Expiry</Table.Th>
                  <Table.Th>Points earned per 1 {domain.currency}</Table.Th>
                  <Table.Th>On refund</Table.Th>
                  <Table.Th></Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {loyaltyGrants.map((grant, index) => (
                  <Table.Tr key={index}>
                    <Table.Td>
                      <EventIcon eventName={grant.matchEvent} />{' '}
                      {eventTypes.find((e) => e.value === grant.matchEvent)?.label || prettifiedLabel(grant.matchEvent)}
                    </Table.Td>
                    <Table.Td>
                      {(grant.matchTags || []).map((tag, i) => (
                        <Badge key={i} variant="default">
                          {tag}
                        </Badge>
                      ))}
                    </Table.Td>
                    <Table.Td>{grant.expiry === 'duration' ? `${grant.validDays} days` : grant.expiry}</Table.Td>
                    <Table.Td>{grant.grantAmount.toLocaleString('en-US')}</Table.Td>
                    <Table.Td>
                      {grant.reversibleOnRefund === false && (
                        <Badge variant="light" color="gray">
                          Points kept
                        </Badge>
                      )}
                    </Table.Td>
                    <Table.Td>
                      <Group gap="sm" justify="flex-end">
                        <LockableButton
                          restrictionKey="loyaltyUpdate"
                          variant="default"
                          size="sm"
                          mr="md"
                          onClick={() => setLoyaltyGrant(grant)}
                        >
                          <IconPencil size={14} />
                        </LockableButton>
                        <LockableButton
                          restrictionKey="loyaltyDelete"
                          color="red"
                          size="sm"
                          onClick={() => setShowDeleteId(grant.id)}
                        >
                          <IconTrash size={14} />
                        </LockableButton>
                      </Group>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          )}
          <LockableButton
            fullWidth
            restrictionKey="loyaltyCreate"
            onClick={() =>
              setLoyaltyGrant({
                type: 'valueBased',
                expiry: 'duration',
                matchTags: [],
                validDays: 90,
                grantAmount: 50,
                reversibleOnRefund: true,
              })
            }
          >
            <IconPlus size={16} /> Add Rule
          </LockableButton>
        </Card.Section>
      </Card>

      {userError && <ErrorMessage message={userError.message} />}

      <LoyaltyGrantModal
        currency={domain.currency}
        loyaltyGrant={loyaltyGrant}
        onClose={() => setLoyaltyGrant(undefined)}
        onSave={(loyaltyGrant) => {
          let matched = false;
          const newLoyaltyGrants = [];
          for (const grant of loyaltyGrants) {
            if (grant.id === loyaltyGrant.id) {
              newLoyaltyGrants.push(loyaltyGrant);
              matched = true;
            } else {
              newLoyaltyGrants.push(grant);
            }
          }

          if (!matched) {
            newLoyaltyGrants.push(loyaltyGrant);
          }
          setLoyaltyGrants(newLoyaltyGrants);
        }}
      />
    </>
  );
};

export default LoyaltyGrantsTab;
